import { cutRouteAtFootIndex } from './plain-walk-scope.js';
import type { PlainWalkResult } from './plain-walk.js';
import type { StealthWalkEvidence } from './stealth-walk.js';
import { legacyStepPositions } from '../zones/walk-zone-crossings.js';

export interface StealthConsequences {
  trapStep: number | null;
  interleavedDice: boolean;
  teleportOrTraversal: boolean;
}

/** MV10 (2026-10-08): end-of-walk cleanup/pricing is dice-free; only a trap's draws follow ALL visited stealth feet. */
export function classifyStealthConsequences(walk: PlainWalkResult): StealthConsequences {
  return { trapStep: walk.trap?.step ?? null,
    interleavedDice: !!(walk.overrun?.triggers.length || walk.zone_entries?.length || walk.follower_zone_entries?.some(f => !f.at) || walk.move_trigger),
    teleportOrTraversal: !!(walk.teleport || walk.traversal) };
}

/** Include the first unpaid foot, then stop at the trap foot (stealth precedes its sensor). */
export function stealthWalkFeet(walk: PlainWalkResult, requestedRoute: ReadonlyArray<readonly number[]>): Array<[number, number]> {
  let route: ReadonlyArray<readonly number[]> = walk.route;
  if (walk.truncated) {
    const paidFeet = legacyStepPositions(walk.route), requestedFeet = legacyStepPositions(requestedRoute);
    if (requestedFeet[paidFeet.length]) route = cutRouteAtFootIndex(requestedRoute, paidFeet.length);
  }
  const feet = legacyStepPositions(route) as Array<[number, number]>;
  return walk.trap ? feet.slice(0, walk.trap.step + 1) : feet;
}

export function stealthCompositionReason(consequences: StealthConsequences, touched: boolean, fresh: boolean, moverPc: boolean): string | null {
  if (touched && consequences.teleportOrTraversal) return 'stealth:with-teleport-or-traversal';
  if (fresh && consequences.interleavedDice) return 'stealth:fresh-contest-with-dice-consequence';
  // All supported trap payloads roll the victim's skill. PC ascension is not seeded in MV10.
  if (moverPc && consequences.trapStep !== null) return 'stealth:pc-chain-with-trap';
  return null;
}

/** A consequence halting on an earlier foot suppresses the later cache-only reveal. Fresh evidence here is a composition defect. */
export function stealthEvidenceAfterHalt(evidence: StealthWalkEvidence | undefined, haltStep: number): StealthWalkEvidence | undefined {
  if (!evidence) return evidence;
  if (evidence.halt_step !== null && haltStep < evidence.halt_step) {
    if (evidence.contests.length !== 0) throw new Error('stealth:drop-with-fresh');
    return undefined;
  }
  return evidence;
}
